import React, { useState } from "react";
import { Minus, Plus, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import CheckoutDialog from "@/components/CheckoutDialog";

export default function TicketBooking({ ev }) {
  const tiers = (ev.tiers || []).filter((t) => t.name !== "Archived");
  const [sel, setSel] = useState(tiers[0]?.name || "");
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [checkout, setCheckout] = useState(null);

  if (!tiers.length) return <p className="text-neutral-500 text-sm">This event has ended. Follow us for the next one.</p>;

  const selected = tiers.find((t) => t.name === sel);
  const isFree = selected && Number(selected.price) === 0;
  const left = selected ? Math.max(0, (selected.capacity || 0) - (selected.sold || 0)) : 0;
  const subtotal = selected ? Number(selected.price) * qty : 0;
  const fee = subtotal > 0 ? subtotal * 0.03 : 0;
  const total = (subtotal + fee).toFixed(2);

  const submit = async () => {
    if (!name || !email) { toast.error("Add your name and email for the tickets."); return; }
    if (isFree) {
      setLoading(true);
      try {
        const { data } = await api.post("/tickets/free", { event_id: ev.id, tier_name: sel, quantity: qty, name, email, origin_url: window.location.origin });
        setDone(data);
        toast.success("You're on the list! Check your email.");
      } catch { toast.error("Something went wrong. Try again."); }
      setLoading(false);
    } else {
      const hasPk = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (hasPk) {
        // Embedded Stripe checkout (inline, no redirect)
        setCheckout({ event_id: ev.id, tier_name: sel, quantity: qty, name, email, origin_url: window.location.origin, embedded: true });
      } else {
        // Fallback: hosted Stripe checkout redirect (works without a publishable key)
        setLoading(true);
        try {
          const { data } = await api.post("/payments/checkout", { event_id: ev.id, tier_name: sel, quantity: qty, name, email, origin_url: window.location.origin, embedded: false });
          window.location.href = data.checkout_url;
          return;
        } catch { toast.error("Something went wrong. Try again."); }
        setLoading(false);
      }
    }
  };

  if (done) {
    return (
      <div data-testid="ticket-free-success" className="text-center py-4">
        <CheckCircle2 size={48} className="text-neutral-900 mx-auto mb-4" />
        <h3 className="display-mega text-2xl">You're on the list</h3>
        <p className="text-neutral-500 text-sm mt-2">{done.tier_name} × {done.quantity} — saved for {email}. Bring your ID; you're on the guest list under your name.</p>
      </div>
    );
  }

  return (
    <div data-testid="ticket-booking">
      <div className="space-y-2.5">
        {tiers.map((t) => {
          const tleft = Math.max(0, (t.capacity || 0) - (t.sold || 0));
          const active = sel === t.name;
          const soldout = t.capacity > 0 && tleft <= 0;
          const free = Number(t.price) === 0;
          return (
            <div
              key={t.name}
              data-testid={`tier-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
              role="button"
              tabIndex={soldout ? -1 : 0}
              onClick={() => { if (!soldout) { setSel(t.name); setQty(1); } }}
              onKeyDown={(e) => { if (!soldout && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setSel(t.name); setQty(1); } }}
              className={`w-full text-left rounded-2xl border p-4 transition-all cursor-pointer ${active ? "border-neutral-900 bg-black/[0.03]" : "border-black/10 hover:border-black/25"} ${soldout ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-neutral-900">{t.name}</div>
                  {t.description && <div className="text-neutral-500 text-xs mt-0.5">{t.description}</div>}
                  {t.capacity > 0 && !soldout && <div className="text-neutral-900 text-xs mt-0.5 font-medium">{tleft} left</div>}
                  {soldout && <div className="text-neutral-400 text-xs mt-0.5">Sold out</div>}
                </div>
                <div className="text-right shrink-0">
                  <span className="display-mega text-xl text-neutral-900 block leading-none">{free ? "FREE" : `$${t.price}`}</span>
                  {!free && <span className="text-neutral-400 text-[10px] uppercase tracking-wide">deposit</span>}
                </div>
              </div>
              {active && !soldout && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/10" onClick={(e) => e.stopPropagation()}>
                  <span className="text-neutral-500 text-sm">Quantity</span>
                  <div className="flex items-center gap-3">
                    <button type="button" data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10"><Minus size={15} /></button>
                    <span className="font-display font-bold text-lg w-6 text-center">{qty}</span>
                    <button type="button" data-testid="qty-plus" onClick={() => setQty((q) => Math.min(Math.max(1, left || 20), q + 1))} className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10"><Plus size={15} /></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-2.5 mt-4">
        <input data-testid="ticket-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full bg-neutral-100 border border-black/10 rounded-xl px-4 py-3 text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900" />
        <input data-testid="ticket-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for tickets" className="w-full bg-neutral-100 border border-black/10 rounded-xl px-4 py-3 text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900" />
      </div>

      <div className="flex items-center justify-between mt-5">
        <div>
          <span className="text-neutral-500 text-xs uppercase tracking-widest">{isFree ? "Total" : "Due now"}</span>
          <div className="display-mega text-3xl text-neutral-900">{isFree ? "FREE" : `$${total}`}</div>
          {!isFree && <span className="text-neutral-400 text-xs">${subtotal.toFixed(2)} deposit + 3% processing fee · balance at the venue</span>}
        </div>
        <button data-testid="checkout-pay" onClick={submit} disabled={loading} className="btn-gold">
          {loading ? <Loader2 className="animate-spin" size={18} /> : null} {loading ? "Please wait…" : (isFree ? "Reserve Free" : "Reserve Bottle")} {!loading && <ArrowRight size={16} />}
        </button>
      </div>
      {isFree
        ? <p className="text-center text-neutral-400 text-xs mt-3">Free entry · No card needed · Confirmation sent to your email</p>
        : <p className="text-center text-neutral-400 text-xs mt-3">Secure checkout powered by Stripe · Only the deposit is charged now</p>}
      {checkout && <CheckoutDialog payload={checkout} onClose={() => setCheckout(null)} />}
    </div>
  );
}
